import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, ChevronRight, CircleCheck, ClipboardCheck, FlaskConical, ScanLine, Workflow } from 'lucide-react';
import {
  loadLaboratoryApprovals, loadLaboratoryDevices, loadLaboratorySamples, loadLaboratoryWork, loadLaboratoryWorkflows,
} from './api';
import { TubeProgress } from './LaboratoryCollectionModal';
import { LabResource, labWhen } from './LaboratoryParts';
import { departmentShort, ownSampleTypeName, stageName, workflowName } from './LaboratoryText';
import { NUMBER, Panel } from './MobileParts';
import { DeviceArt } from './XomicsLabDevices';
import { Empty, Head, Pill } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './laboratory-inventory.css';
import './laboratory-reference.css';

const text = (key, values) => tx(`ui.lab.overview.${key}`, values);
const LISTED = 5;
const DEVICE_TONES = { in_service: 'ok', out_of_service: 'bad', not_used: 'flat' };

// The lab's day as one line: where every collection is waiting, in the order
// the work happens. Each step opens the screen that does it.
function Flow({ samples, work, approvals }) {
  const waiting = approvals.stageRecords.length + approvals.acceptances.length;
  const steps = [
    { code: 'accept', Icon: ClipboardCheck, to: 'samples', count: samples.statusCounts.awaiting_acceptance },
    { code: 'assign', Icon: Workflow, to: 'samples', count: samples.statusCounts.ready },
    { code: 'run', Icon: FlaskConical, to: 'work', count: work.statusCounts.planned + work.statusCounts.in_progress },
    { code: 'approve', Icon: BadgeCheck, to: 'approvals', count: waiting },
    { code: 'done', Icon: CircleCheck, to: 'work', count: work.statusCounts.completed },
  ];
  const held = [['on_hold', samples.statusCounts.on_hold, 'warn'], ['quarantined', samples.statusCounts.quarantined, 'bad'],
    ['stopped', work.statusCounts.stopped, 'bad']].filter(([, count]) => count > 0);
  return <section className="xo-labov-flow" aria-label={text('flow')}>
    <ol>{steps.map(step => <li key={step.code} className={step.count > 0 && step.code !== 'done' ? 'is-waiting' : ''}>
      <Link to={`/xomics/laboratory/${step.to}`}>
        <span className="xo-labov-step-icon"><step.Icon aria-hidden="true" /></span>
        <span className="xo-labov-step-label">{text(`step.${step.code}`)}</span>
        <strong>{NUMBER.format(step.count)}</strong>
        <small>{text(`step.${step.code}_detail`)}</small>
      </Link>
    </li>)}</ol>
    {held.length > 0 && <p className="xo-labov-held">{held.map(([code, count, tone]) => <Pill key={code} tone={tone}>{text(`held.${code}`, { count })}</Pill>)}</p>}
  </section>;
}

function Waiting({ samples }) {
  if (samples.samples.length === 0) return <Empty title={text('waiting_empty')} />;
  return <ul className="xo-labov-list">{samples.samples.map(row => <li key={row.collectionUuid}>
    <Link to="/xomics/laboratory/samples">
      <span className="xo-labov-main"><code>{row.code}</code><small>{row.product === null ? ownSampleTypeName(row.sampleType) : row.product.name}</small></span>
      <TubeProgress samples={row.physicalSamples} />
      <span className="xo-labov-when">{labWhen(row.lastReceivedAt)}</span>
      <ChevronRight aria-hidden="true" />
    </Link>
  </li>)}</ul>;
}

function Running({ work }) {
  const orders = work.collections.flatMap(collection => collection.workOrders.map(order => ({ ...order, collection })));
  if (orders.length === 0) return <Empty title={text('running_empty')} detail={text('running_empty_detail')} />;
  return <ul className="xo-labov-list">{orders.slice(0, LISTED).map(order => <li key={order.uuid}>
    <Link to={`/xomics/laboratory/work?collection=${order.collection.uuid}`}>
      <span className="xo-labov-code">{order.workflow.code}</span>
      <span className="xo-labov-main"><code>{order.tube.code}</code>
        <small>{order.currentStage === null ? tx(`ui.lab.stage.order.${order.status}`) : stageName(order.currentStage.code)}</small></span>
      <span className="xo-labov-progress" role="img" aria-label={tx('ui.lab.work.progress', { done: order.completedSteps, total: order.totalSteps })}>
        {order.stages.map(stage => <i key={stage.id} className={`is-${stage.state}`} />)}</span>
      <ChevronRight aria-hidden="true" />
    </Link>
  </li>)}</ul>;
}

function Workflows({ workflows }) {
  return <div className="xo-flow-tiles">{workflows.map(workflow => {
    const current = workflow.versions.find(version => version.status === 'published') || workflow.versions[0];
    return <Link key={workflow.code} className="xo-flow-tile" to={`/xomics/laboratory/workflow-definitions?workflow=${workflow.code}`}>
      <span className="xo-flow-tile-code">{workflow.code}</span>
      <strong>{workflowName(workflow.code)}</strong>
      <span className="xo-flow-tile-steps" aria-hidden="true">{Array.from({ length: current.stageCount }, (_, index) => <i key={index} />)}</span>
      <small title={departmentShort(workflow.departmentCode)}>{workflow.departmentCode} · {tx('ui.lab.flow.stage_count', { count: current.stageCount })}</small>
    </Link>;
  })}</div>;
}

function Equipment({ devices }) {
  const inService = devices.filter(device => device.status === 'in_service');
  const others = devices.filter(device => device.status !== 'in_service');
  return <div className="xo-labov-equipment">
    <div className="xo-labov-figure"><strong>{inService.length}<small> / {devices.length}</small></strong><span>{tx('ui.lab.machines.in_service')}</span>
      <span className="xo-labov-meter" aria-hidden="true"><i style={{ width: `${devices.length ? (inService.length / devices.length) * 100 : 0}%` }} /></span></div>
    {others.length === 0 ? <p className="xo-lab-muted">{tx('ui.lab.machines.all_in_service')}</p>
      : <ul className="xo-labov-devices">{others.map(device => <li key={device.id}>
        <Link to={`/xomics/laboratory/devices?device=${device.id}`}>
          <span className="xo-labov-device-art"><DeviceArt device={device} /></span>
          <span className="xo-labov-main"><strong>{device.name}</strong><small>{device.inventoryNumber}</small></span>
          <Pill tone={DEVICE_TONES[device.status]}>{tx(`ui.lab.device.status.${device.status}`)}</Pill>
          <ChevronRight aria-hidden="true" />
        </Link>
      </li>)}</ul>}
  </div>;
}

const loadWaiting = () => loadLaboratorySamples({ limit: LISTED, status: 'awaiting_acceptance' });

export default function XomicsLabOverview() {
  const loadWork = useCallback(() => loadLaboratoryWork({ limit: LISTED, offset: 0, status: 'in_progress' }), []);
  const samples = useLiveResource(['samples'], loadWaiting);
  const work = useLiveResource(['samples'], loadWork);
  const approvals = useLiveResource(['samples'], loadLaboratoryApprovals);
  const devices = useLiveResource(['laboratory'], loadLaboratoryDevices);
  const workflows = useLiveResource(['laboratory'], loadLaboratoryWorkflows);
  const laboratory = samples.data === null ? null : samples.data.laboratory;
  const more = (to, label) => <Link className="xo-mob-link" to={`/xomics/laboratory/${to}`}>{label}<ChevronRight aria-hidden="true" /></Link>;
  return <div className="xo-platform-page xo-lab-page xo-web-page xo-labov">
    <Head title={text('title')} subtitle={laboratory === null ? text('subtitle') : laboratory.name}>
      <Link className="xo-btn" to="/xomics/laboratory/receipt"><ScanLine aria-hidden="true" />{text('receive')}</Link>
    </Head>
    <LabResource resource={samples}>{sampleData => <LabResource resource={work}>{workData => <LabResource resource={approvals}>{approvalData => <>
      <Flow samples={sampleData} work={workData} approvals={approvalData} />
      <div className="xo-mob-row is-halves is-even">
        <Panel title={text('waiting_title')} detail={text('waiting_detail')} aside={more('samples', tx('ui.nav.sample_inventory'))}>
          <Waiting samples={sampleData} />
        </Panel>
        <Panel title={text('running_title')} detail={text('running_detail')} aside={more('work', tx('ui.lab.work.title'))}>
          <Running work={workData} />
        </Panel>
      </div>
    </>}</LabResource>}</LabResource>}</LabResource>
    <div className="xo-mob-row is-wide-left is-even">
      <Panel title={tx('ui.lab.flow.title')} detail={text('workflows_detail')}>
        <LabResource resource={workflows}>{data => <Workflows workflows={data.workflows} />}</LabResource>
      </Panel>
      <Panel title={tx('ui.lab.machines.title')} detail={tx('ui.lab.machines.overview_detail')} aside={more('devices', tx('ui.lab.machines.title'))}>
        <LabResource resource={devices}>{data => <Equipment devices={data.devices} />}</LabResource>
      </Panel>
    </div>
  </div>;
}
